/**
 * Document numbering utility.
 *
 * Supports configurable prefixes for all document types:
 *   - Invoice (INV-), Sales Order (SO-), Purchase Order (PO-),
 *     Credit Note (CN-), Payment (PAY-), Expense (EXP-)
 *
 * Formats:
 *   - Normal: {prefix}{year}-{padded_counter}  e.g. "INV-2026-000001"
 *   - NCS imported: "NCS-{original_number}"     e.g. "NCS-2193"
 *   - Draft: "" (empty string, number assigned on issue)
 */

import { useMemo } from "react";
import { useAppSettings } from "./appSettings";
import { usePersistentMockState } from "./usePersistentMockState";

/* ─────────────────────────────────────────────────────────────
 * Document types & defaults — re-exported from documentTypes.ts
 * to avoid circular dependency (appSettings also imports these).
 * ───────────────────────────────────────────────────────────── */

export {
  DOCUMENT_TYPES,
  DEFAULT_PREFIXES,
  DOCUMENT_TYPE_LABELS,
  type DocumentType,
  type DocumentPrefixes,
} from "./documentTypes";

import {
  DEFAULT_PREFIXES,
  type DocumentType,
} from "./documentTypes";

/* ─────────────────────────────────────────────────────────────
 * Legacy config type (kept for back-compat with existing
 * invoice numbering settings)
 * ───────────────────────────────────────────────────────────── */

export type InvoiceNumberConfig = {
  prefix: string; // e.g. "AC", "INV"
  yearFormat: "full" | "short"; // "full" = 2026, "short" = 26
  padLength: number; // e.g. 6 -> 000001
};

/* ─────────────────────────────────────────────────────────────
 * Pure functions
 * ───────────────────────────────────────────────────────────── */

/**
 * Format an invoice number from its parts.
 */
export function formatInvoiceNumber(
  config: InvoiceNumberConfig,
  year: number,
  counter: number,
): string {
  const yearStr =
    config.yearFormat === "short" ? String(year).slice(-2) : String(year);
  const counterStr = String(counter).padStart(config.padLength, "0");
  return `${config.prefix}-${yearStr}-${counterStr}`;
}

/**
 * Parse an invoice number to extract prefix, year, and counter.
 * Returns null if the format doesn't match.
 */
export function parseInvoiceNumber(
  invoiceNumber: string,
): {
  prefix: string;
  year: number;
  counter: number;
} | null {
  const match = invoiceNumber.match(/^(.+)-(\d{2,4})-(\d+)$/);
  if (!match) return null;
  const [, prefix, yearStr, counterStr] = match;
  const year =
    yearStr!.length === 2 ? 2000 + Number(yearStr) : Number(yearStr);
  return { prefix: prefix!, year, counter: Number(counterStr) };
}

/**
 * Determine the next invoice number given existing invoices.
 * Only counts invoices matching the current prefix+year.
 */
export function nextInvoiceNumber(
  config: InvoiceNumberConfig,
  existingNumbers: string[],
  year?: number,
): string {
  const targetYear = year ?? new Date().getFullYear();
  const yearStr =
    config.yearFormat === "short"
      ? String(targetYear).slice(-2)
      : String(targetYear);
  const prefix = `${config.prefix}-${yearStr}-`;

  let maxCounter = 0;
  for (const num of existingNumbers) {
    if (num.startsWith(prefix)) {
      const parsed = parseInvoiceNumber(num);
      if (parsed && parsed.counter > maxCounter) {
        maxCounter = parsed.counter;
      }
    }
  }
  return formatInvoiceNumber(config, targetYear, maxCounter + 1);
}

/**
 * Check if an invoice number is from NCS import.
 */
export function isNcsInvoiceNumber(invoiceNumber: string): boolean {
  return invoiceNumber.startsWith("NCS-");
}

/**
 * Generate a preview of what the next invoice number will look like.
 * Useful for settings UI preview.
 */
export function previewInvoiceNumber(config: InvoiceNumberConfig): string {
  return formatInvoiceNumber(config, new Date().getFullYear(), 1);
}

/* ─────────────────────────────────────────────────────────────
 * Generic document numbering
 *
 * Format: {PREFIX}{YEAR}-{SEQUENTIAL_NUMBER}
 * Example: INV-2026-000001, SO-2026-000042
 * ───────────────────────────────────────────────────────────── */

/**
 * Generate a document number from a prefix, year, and counter.
 * The prefix already includes a trailing separator (e.g. "INV-"),
 * so the result is: INV-2026-000001
 */
export function generateNumber(
  _type: DocumentType,
  existingNumbers: string[],
  prefix: string,
  year?: number,
): string {
  const targetYear = year ?? new Date().getFullYear();
  // The prefix includes a trailing dash, e.g. "INV-"
  // So the search pattern is "INV-2026-"
  const searchPrefix = `${prefix}${targetYear}-`;

  let maxCounter = 0;
  for (const num of existingNumbers) {
    if (num.startsWith(searchPrefix)) {
      // Extract the counter portion after the year segment
      const counterStr = num.slice(searchPrefix.length);
      const counter = Number(counterStr);
      if (!Number.isNaN(counter) && counter > maxCounter) {
        maxCounter = counter;
      }
    }
  }

  const nextCounter = maxCounter + 1;
  const paddedCounter = String(nextCounter).padStart(6, "0");
  return `${prefix}${targetYear}-${paddedCounter}`;
}

/**
 * Generate a preview of the next document number for a given prefix.
 */
export function previewDocumentNumber(prefix: string): string {
  const year = new Date().getFullYear();
  return `${prefix}${year}-000001`;
}

/**
 * Parse a document number formatted as {PREFIX}{YEAR}-{COUNTER}.
 * Returns null if the format doesn't match.
 */
export function parseDocumentNumber(
  docNumber: string,
): {
  prefix: string;
  year: number;
  counter: number;
} | null {
  // Match: everything up to and including the trailing dash of the prefix,
  // then a 4-digit year, a dash, and a numeric counter.
  const match = docNumber.match(/^(.+?)(\d{4})-(\d+)$/);
  if (!match) return null;
  const [, prefix, yearStr, counterStr] = match;
  return {
    prefix: prefix!,
    year: Number(yearStr),
    counter: Number(counterStr),
  };
}

/* ─────────────────────────────────────────────────────────────
 * Entity-number extractors
 *
 * Each document entity stores its number in a different field.
 * These helpers retrieve the number strings needed by the
 * generateNumber function.
 * ───────────────────────────────────────────────────────────── */

/** Map from DocumentType to the mock state storage key + the field
 *  on each entity that holds the document number string. */
const ENTITY_NUMBER_FIELDS: Record<
  DocumentType,
  { stateKey: string; numberField: string }
> = {
  invoice: { stateKey: "invoices", numberField: "invoiceNumber" },
  salesOrder: { stateKey: "salesOrders", numberField: "orderNumber" },
  purchaseOrder: { stateKey: "purchaseOrders", numberField: "orderNumber" },
  creditNote: { stateKey: "creditNotes", numberField: "creditNoteNumber" },
  payment: { stateKey: "payments", numberField: "paymentNumber" },
  expense: { stateKey: "expenses", numberField: "expenseNumber" },
};

/**
 * React hook that returns the next document number for a given type.
 *
 * Reads the current prefix from app settings, scans existing entities
 * to find the highest counter, and returns the next sequential number.
 */
export function useNextNumber(type: DocumentType): string {
  const { companyProfile } = useAppSettings();

  const prefixes = companyProfile.documentPrefixes ?? DEFAULT_PREFIXES;
  const prefix = prefixes[type];

  const { stateKey, numberField } = ENTITY_NUMBER_FIELDS[type];

  // Read existing entities — we use an empty array as the seed since
  // the actual data is loaded from persistent state.
  const [entities] = usePersistentMockState<Record<string, unknown>[]>(
    stateKey,
    [],
  );

  const nextNum = useMemo(() => {
    const existingNumbers = entities
      .map((e) => e[numberField])
      .filter((n): n is string => typeof n === "string" && n.length > 0);
    return generateNumber(type, existingNumbers, prefix);
  }, [entities, numberField, prefix, type]);

  return nextNum;
}
